/*
 *  Agency-wide tier lookup for the avatar ring.
 *
 *  Fetched once per session rather than per avatar: a page can easily render
 *  thirty of them. The endpoint returns only silver-and-above, so the map
 *  itself cannot leak the bottom of the board, and a 403 (the agency switched
 *  the board off) simply yields an empty map — every avatar then renders
 *  exactly as it did before this feature existed.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "@/api/client";

interface TierBadge {
  user_id: string;
  tier: string;
}

const TierCtx = createContext<Record<string, string>>({});

export function TierProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    api<TierBadge[]>("/api/leaderboard/tiers")
      .then((rows) => {
        if (!alive) return;
        const next: Record<string, string> = {};
        for (const r of rows) next[r.user_id] = r.tier;
        setMap(next);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return <TierCtx.Provider value={map}>{children}</TierCtx.Provider>;
}

/** The tier for a user id, or undefined — which is the common case and must
 *  render as a plain avatar. */
export function useTier(userId: string | null | undefined): string | undefined {
  const map = useContext(TierCtx);
  return userId ? map[userId] : undefined;
}
